import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { 
  Package, 
  Users, 
  Route, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Plus,
  ArrowRight
} from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'
import { todayInManaus } from '../../utils/dateManaus'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'

// Busca estatísticas do dia
const fetchTodayStats = async () => {
  const today = todayInManaus()
  const response = await api.get('/api/deliveries', {
    params: { date: today }
  })
  
  const deliveries = response.data.data.deliveries || []
  
  return {
    total: deliveries.length,
    pending: deliveries.filter(d => d.status === 'aguardando_saida').length,
    inRoute: deliveries.filter(d => d.status === 'em_rota').length,
    delivered: deliveries.filter(d => d.status === 'entregue').length,
    failed: deliveries.filter(d => d.status === 'nao_entregue').length,
    deliveries,
  }
}

// Card de estatística
function StatCard({ icon: Icon, label, value, color = 'primary' }) {
  const colors = {
    primary: 'bg-primary-100 text-primary-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600',
  }

  return (
    <Card className="flex items-center gap-4">
      <div className={`p-3 rounded-lg ${colors[color]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </Card>
  )
}

// Status badge
function StatusBadge({ status }) {
  const statusConfig = {
    aguardando_saida: { label: 'Aguardando', color: 'bg-gray-100 text-gray-700' },
    em_rota: { label: 'Em rota', color: 'bg-blue-100 text-blue-700' },
    entregue: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
    nao_entregue: { label: 'Não entregue', color: 'bg-red-100 text-red-700' },
    cancelada: { label: 'Cancelada', color: 'bg-gray-100 text-gray-500' },
  }

  const config = statusConfig[status] || statusConfig.aguardando_saida

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
      {config.label}
    </span>
  )
}

export default function Dashboard() {
  const { user } = useAuth()
  
  const { data: stats, isLoading } = useQuery({
    queryKey: ['todayStats'],
    queryFn: fetchTodayStats,
    refetchInterval: 30000, // Atualiza a cada 30s
  })

  const greeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Bom dia'
    if (hour < 18) return 'Boa tarde'
    return 'Boa noite'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">
            {greeting()}, {user?.name?.split(' ')[0]}! 👋
          </h1>
          <p className="page-subtitle">
            Aqui está o resumo das suas entregas de hoje.
          </p>
        </div>
        <Link to="/routes/new">
          <Button>
            <Plus className="w-4 h-4" />
            Nova Rota
          </Button>
        </Link>
      </div>

      {/* Cards de estatísticas */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 animate-pulse rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            icon={Package} 
            label="Total do dia" 
            value={stats?.total || 0} 
          />
          <StatCard 
            icon={Clock} 
            label="Aguardando" 
            value={stats?.pending || 0} 
            color="yellow"
          />
          <StatCard 
            icon={Route} 
            label="Em rota" 
            value={stats?.inRoute || 0} 
            color="primary"
          />
          <StatCard 
            icon={CheckCircle2} 
            label="Entregues" 
            value={stats?.delivered || 0} 
            color="green"
          />
        </div>
      )}

      {/* Entregas do dia */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Entregas de Hoje</h2>
            <p className="text-sm text-gray-500">
              {new Date().toLocaleDateString('pt-BR', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long' 
              })}
            </p>
          </div>
          <Link 
            to="/deliveries" 
            className="text-sm text-primary-600 hover:underline flex items-center gap-1"
          >
            Ver todas <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : stats?.deliveries?.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">Nenhuma entrega para hoje</p>
            <Link to="/deliveries/new">
              <Button variant="outline" size="sm">
                <Plus className="w-4 h-4" />
                Criar entrega
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {stats?.deliveries?.slice(0, 5).map((delivery) => (
              <div 
                key={delivery.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {delivery.recipient?.name || 'Destinatário'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {delivery.recipient?.address?.street || delivery.recipient?.addressStreet}, {delivery.recipient?.address?.number || delivery.recipient?.addressNumber} - {delivery.recipient?.address?.neighborhood || delivery.recipient?.addressNeighborhood}
                    </p>
                  </div>
                </div>
                <StatusBadge status={delivery.status} />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Ações rápidas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link to="/recipients/new">
          <Card className="hover:border-primary-300 hover:shadow-md transition-all cursor-pointer">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Novo Destinatário</p>
                <p className="text-sm text-gray-500">Cadastrar cliente</p>
              </div>
            </div>
          </Card>
        </Link>

        <Link to="/deliveries/new">
          <Card className="hover:border-primary-300 hover:shadow-md transition-all cursor-pointer">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Package className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Nova Entrega</p>
                <p className="text-sm text-gray-500">Agendar entrega</p>
              </div>
            </div>
          </Card>
        </Link>

        <Link to="/routes/new">
          <Card className="hover:border-primary-300 hover:shadow-md transition-all cursor-pointer">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Route className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Nova Rota</p>
                <p className="text-sm text-gray-500">Otimizar entregas</p>
              </div>
            </div>
          </Card>
        </Link>
      </div>
    </div>
  )
}
